import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { ShieldCheck, ShieldX, Coins } from 'lucide-react';
import { fetchDashboard, fetchVerifications } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { StatusBadge } from '../components/ui/badge';
import { daysAgo, today, formatDate } from '../lib/utils';
import { useLang } from '../lib/i18n';

const STATUS_COLORS: Record<string, string> = {
  green: '#22c55e', red: '#ef4444', expired: '#94a3b8',
  pending: '#f59e0b', out_of_credits: '#f97316',
};

function StatCard({ label, value, icon: Icon, color }: {
  label: string; value: string | number; icon: React.ElementType; color: string;
}) {
  return (
    <Card className="flex items-center gap-4 px-6 py-5">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const { t } = useLang();
  const from = daysAgo(30);
  const to   = today();

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['dashboard', from, to],
    queryFn: () => fetchDashboard(from, to),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: recent } = useQuery({
    queryKey: ['verifications-recent', from, to],
    queryFn: () => fetchVerifications(from, to, 10),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (statsError) {
    const msg = (statsError as any)?.response?.data?.error ?? (statsError as Error).message;
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-red-600 font-semibold">{t.dashboard.loadError}</p>
          <p className="text-sm text-gray-500">{msg}</p>
        </div>
      </div>
    );
  }

  const total      = stats?.total ?? 0;
  const green      = stats?.by_status?.green ?? 0;
  const red        = stats?.by_status?.red   ?? 0;
  const credits    = stats?.credits_remaining ?? 0;
  const successPct = total > 0 ? Math.round((green / total) * 100) : 0;

  const pieData = Object.entries(stats?.by_status ?? {}).map(([key, val]) => ({
    name: key, value: val as number,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t.dashboard.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{t.dashboard.subtitle}</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t.dashboard.totalVerifications} value={total}            icon={ShieldCheck} color="bg-brand-600" />
        <StatCard label={t.dashboard.successPct}         value={`${successPct}%`} icon={ShieldCheck} color="bg-green-500" />
        <StatCard label={t.dashboard.rejected}           value={red}              icon={ShieldX}     color="bg-red-500" />
        <StatCard label={t.dashboard.credits}            value={credits}          icon={Coins}       color="bg-yellow-500" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>{t.dashboard.verificationsPerDay}</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.verifications_per_day ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip labelFormatter={d => formatDate(String(d))} />
                <Line type="monotone" dataKey="count" stroke="#0057c7" strokeWidth={2} dot={false} name={t.dashboard.verifications} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t.dashboard.statusDistribution}</CardTitle></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="45%" outerRadius={70}>
                  {pieData.map(entry => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name] ?? '#94a3b8'} />
                  ))}
                </Pie>
                <Legend formatter={(val: string) => t.status[val as keyof typeof t.status] ?? val} />
                <Tooltip formatter={(val, name) => [val, t.status[name as keyof typeof t.status] ?? name]} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent verifications */}
      <Card>
        <CardHeader><CardTitle>{t.dashboard.recentVerifications}</CardTitle></CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
                <th className="px-6 py-3 text-left font-medium">{t.dashboard.cols.name}</th>
                <th className="px-6 py-3 text-left font-medium">{t.dashboard.cols.company}</th>
                <th className="px-6 py-3 text-left font-medium">{t.dashboard.cols.contact}</th>
                <th className="px-6 py-3 text-left font-medium">{t.dashboard.cols.status}</th>
                <th className="px-6 py-3 text-left font-medium">{t.dashboard.cols.document}</th>
                <th className="px-6 py-3 text-left font-medium">{t.dashboard.cols.date}</th>
              </tr>
            </thead>
            <tbody>
              {(recent?.verifications ?? []).map(v => (
                <tr key={v.scanRef} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium text-gray-900">{v.full_name || '—'}</td>
                  <td className="px-6 py-3 text-gray-600">{v.contact_company_name || <span className="text-gray-400 italic">{t.dashboard.notSet}</span>}</td>
                  <td className="px-6 py-3 text-gray-600">{v.contact_name || '—'}</td>
                  <td className="px-6 py-3"><StatusBadge status={v.status} /></td>
                  <td className="px-6 py-3 text-gray-600">{v.doc_type || '—'}</td>
                  <td className="px-6 py-3 text-gray-500">{formatDate(v.timestamp)}</td>
                </tr>
              ))}
              {!recent?.verifications?.length && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                    {t.dashboard.noVerifications}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
